import { Flex, Text } from "shared/components/Spor";
import { MeasureOverviewStateContext } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewStateContext";
import {
  PlanEventInformation,
  PlanMeasureKey,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { InformationOutline18Icon } from "@vygruppen/spor-icon-react";
import { Semantics } from "shared/utils/semantics";
import { ALWAYS_MUTED_STATES } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/useMeasureCardMuteHistory";
import { ActionCardLabel } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/ActionCardLabel";
import { differenceInMilliseconds } from "date-fns";
import { SemanticStaticCard } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/SemanticStaticCard";
import { MuteHistoryEntry } from "api/dropsLocalStorage";
import { MeasureCardHeader } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardHeader";
import { MeasureCardEventBadges } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardEventBadges";
import { MeasureCardFooter } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardFooter";
import { MuteHistory } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MuteHistory";

type MeasureCardContentProps = {
  measure: PlanEventInformation;
  isMuted: boolean;
  semantics: Semantics;
  muteHistory: MuteHistoryEntry[];
  addMuteHistoryEntry: (entry: MuteHistoryEntry) => void;
  measureKey: PlanMeasureKey;
  recentlyUpdated: boolean;
  acknowledgeLatestVersion: () => void;
};

export const MeasureCardContent: FC<MeasureCardContentProps> = ({
  measure,
  isMuted,
  semantics,
  muteHistory,
  addMuteHistoryEntry,
  measureKey,
  recentlyUpdated,
  acknowledgeLatestVersion,
}) => {
  const { compact: globalCompact } = useContext(MeasureOverviewStateContext);
  const [compact, setCompact] = useState(globalCompact);

  const { information, internalMessages } = measure;

  const isClosedOrWithdrawn = ALWAYS_MUTED_STATES.has(information.state);
  const isMuteToggleable =
    measureKey === "noCoverage" &&
    !isClosedOrWithdrawn &&
    semantics !== "warning";

  const latestInternalMessage = useMemo(
    () =>
      internalMessages
        ?.toSorted((a, b) => differenceInMilliseconds(b.updatedAt, a.updatedAt))
        .at(0),
    [internalMessages],
  );

  useEffect(() => {
    setCompact(globalCompact);
  }, [globalCompact]);

  return (
    <Flex direction="column" gap={1}>
      <MeasureCardHeader
        information={information}
        setCompact={setCompact}
        isMuted={isMuted}
        isMuteToggleable={isMuteToggleable}
        addMuteHistoryEntry={addMuteHistoryEntry}
        semantics={semantics}
      />

      <MeasureCardEventBadges
        informationType={information.type}
        informationState={information.state}
        isClosedOrWithdrawn={isClosedOrWithdrawn}
      />

      {!compact && (
        <Flex direction="column" mt={1.5} gap={2}>
          <Text variant="sm">{information.distributions[0].text.message}</Text>
          <ActionCardLabel information={information} semantics={semantics} />

          {latestInternalMessage && (
            <SemanticStaticCard
              semantics="transparent"
              px={2}
              py={1}
              display="flex"
              flexFlow="row nowrap"
              gap={1}
              borderRadius="sm"
              borderColor="outline"
            >
              <Flex height="27px" alignItems="center">
                <InformationOutline18Icon />
              </Flex>
              <Text variant="sm">
                {latestInternalMessage.messages.NOB?.message}
              </Text>
            </SemanticStaticCard>
          )}

          <MuteHistory muteHistory={muteHistory} semantics={semantics} />

          <MeasureCardFooter
            measure={measure}
            recentlyUpdated={recentlyUpdated}
            acknowledgeLatestVersion={acknowledgeLatestVersion}
            semantics={semantics}
          />
        </Flex>
      )}
    </Flex>
  );
};
